import { Controller } from "@hotwired/stimulus"

// The gallery's lightbox (docs/07 Gallery).
//
// One dialog per page, filled from whichever tile was opened: an album of 300
// photographs would otherwise mean 300 dialogs in the document.
//
// Keyboard navigation is the point. A lightbox that only works with a mouse is
// a lightbox that traps a keyboard user inside the first photograph — so the
// arrows move, Escape closes (the dialog element's own behaviour, which we
// listen for rather than reimplement), and focus goes back to the tile that
// opened it.
export default class extends Controller {
  static targets = ["dialog", "image", "caption", "counter", "zone", "sub", "holder"]

  // Focus is NOT restored here: `showModal()` and `close()` already return it
  // to the element that opened the dialog, and a second `focus()` on top of
  // the platform's is code that cannot be told from no code at all (proved by
  // mutation — removing it changed nothing). The system test asserts focus
  // comes back, whoever does it.
  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
  }

  get tiles() { return Array.from(this.element.querySelectorAll("[data-photo]")) }

  open(event) {
    this.opener = event.currentTarget
    // No modal <dialog> before Safari 15.4: open the photo itself.
    if (typeof this.dialogTarget.showModal !== "function") {
      window.location.assign(this.opener.dataset.full)
      return
    }
    this.show(Number(this.opener.dataset.index))
    this.dialogTarget.showModal()
  }

  close() { this.dialogTarget.close() }

  next() { this.move(1) }
  previous() { this.move(-1) }

  // Wraps at both ends: the alternative is an arrow key that silently does
  // nothing, which reads as a broken page rather than as the end of the album.
  move(step) {
    if (!this.dialogTarget.open) return

    const count = this.tiles.length
    if (count === 0) return

    this.show((this.index + step + count) % count)
  }

  show(index) {
    const tile = this.tiles[index]
    if (!tile) return

    this.index = index
    this.imageTarget.src = tile.dataset.full
    // The alt text is the photograph's own, carried from the tile: a lightbox
    // with an empty alt is the one place a screen reader has nothing at all.
    this.imageTarget.alt = tile.querySelector("img")?.alt ?? ""
    this.captionTarget.textContent = tile.dataset.caption ?? ""
    this.captionTarget.hidden = !tile.dataset.caption

    // The design's chrome (P11.18): a counter, the zone chip, and the meta
    // line under the caption. Each is hidden rather than left showing a stale
    // value when the photograph it describes has nothing to say.
    this.counterTarget.textContent = `${index + 1} of ${this.tiles.length}`

    const zone = tile.dataset.zone
    this.zoneTarget.textContent = tile.dataset.zoneLabel ?? ""
    this.zoneTarget.hidden = !tile.dataset.zoneLabel
    // `data-zone` is the identity contract: the stylesheet turns the string
    // into the chip's colour, and a hex here would be the one thing docs/17
    // forbids.
    if (zone) this.zoneTarget.dataset.zone = zone

    // `.ph` carries the zone too, because the design tints the frame behind a
    // photograph that has not loaded yet.
    if (this.hasHolderTarget && zone) this.holderTarget.dataset.zone = zone

    this.subTarget.textContent = tile.dataset.sub ?? ""
    this.subTarget.hidden = !tile.dataset.sub
  }

  onKeydown(event) {
    if (!this.dialogTarget.open) return
    if (event.key === "ArrowRight") { event.preventDefault(); this.next() }
    if (event.key === "ArrowLeft") { event.preventDefault(); this.previous() }
  }
}
